import React, { useContext, useMemo } from 'react';
import { observer } from 'mobx-react';
import CoreAppContext from '@this/src/components/core_app/core_app_context';
import { Input } from '@this/shared/ui/inputs/input/input';
import { isTabikobo } from '@this/src/util';
import type ReserveConfirmStore from '@this/src/domain/reserve_confirm_store';
import type { ShinkansenAddressType } from '@this/domain/reserve_info2';
import SelectDepartments from '@this/src/components/reserve_trip/reserve_confirm/routings/input_customer_information/select_departments/select_departments';
import SelectOrganizationBases from '@this/src/components/reserve_trip/reserve_confirm/routings/input_customer_information/select_organization_bases/select_organization_bases';

const WORKFLOW_FORM_WIDTH = '420px';

type Props = {
  store: ReserveConfirmStore;
  isTripsShinkansenInfoRequired: boolean;
  isConfirmStep: boolean;
};

const TripsTicketingMethodSection: React.FC<Props> = ({ store, isTripsShinkansenInfoRequired, isConfirmStep }) => {
  const { serviceId } = useContext(CoreAppContext);
  const styles = useMemo(() => getStyles(), []);
  const organization = store.reserveInfo.user.organization;
  const canSelectTicketingMethod = !!organization?.useQr && !!organization?.usePostal;

  const isExOnlyShinkansenTicketing = () => {
    if (!isTripsShinkansenInfoRequired) return false;
    if (!store.reserveInfo.isSeparateShinkansenType) {
      return store.reserveInfo.ticket === 'ex' || store.reserveInfo.ticket === 'business_ekinet';
    }
    if (store.reserveInfo.reservingTrip.travelers.length === 0) return false;
    return store.reserveInfo.reservingTrip.travelers.every(
      traveler => traveler.shinkansenTicketType === 'ex' || traveler.shinkansenTicketType === 'business_ekinet'
    );
  };

  const updatePreferredTicketingMethod = (value: 'qr' | 'postal') => {
    store.reserveInfo.preferredTicketingMethod = value;
  };

  const showQrGuidance =
    isTripsShinkansenInfoRequired &&
    !isExOnlyShinkansenTicketing() &&
    (canSelectTicketingMethod
      ? store.reserveInfo.preferredTicketingMethod === 'qr'
      : !!organization?.useQr && !organization?.usePostal);

  const showPostalTicketing =
    isTripsShinkansenInfoRequired &&
    !isExOnlyShinkansenTicketing() &&
    (canSelectTicketingMethod
      ? store.reserveInfo.preferredTicketingMethod === 'postal'
      : !!organization?.usePostal && !organization?.useQr);

  if (!isTripsShinkansenInfoRequired || isExOnlyShinkansenTicketing()) {
    return null;
  }

  return (
    <div style={styles.section} data-testid="trips-confirm-ticketing-method-section">
      <div style={styles.subtitle} data-testid="trips-confirm-ticketing-method-subtitle">
        新幹線・特急
      </div>
      <div data-testid="trips-confirm-ticketing-method-content" style={styles.childContent}>
        {canSelectTicketingMethod &&
          (isConfirmStep ? (
            <div style={styles.confirmFieldRow}>
              <div style={styles.confirmFieldLabel} data-testid="trips-confirm-ticketing-method-label">
                発券方法
              </div>
              <div style={styles.confirmFieldValue} data-testid="trips-confirm-ticketing-method-value">
                {store.reserveInfo.preferredTicketingMethod === 'postal'
                  ? 'チケット郵送（自宅・職場に届けてもらう）'
                  : store.reserveInfo.preferredTicketingMethod === 'qr'
                  ? 'QR発券（QRコードで受け取る）'
                  : '未設定'}
              </div>
            </div>
          ) : (
            <div style={styles.fieldBlock}>
              <div style={styles.fieldLabel} data-testid="trips-confirm-ticketing-method-label">
                発券方法
              </div>
              <div style={styles.radioGroup}>
                <label style={styles.travelerType}>
                  <input
                    id="preferred_ticketing_method_qr"
                    data-testid="trips-confirm-ticketing-method-qr"
                    className="companion-label"
                    type="radio"
                    value="qr"
                    onChange={e => updatePreferredTicketingMethod(e.target.value as 'qr' | 'postal')}
                    checked={store.reserveInfo.preferredTicketingMethod === 'qr'}
                  />
                  <span>QR発券（QRコードで受け取る）</span>
                </label>
                <label style={styles.travelerType}>
                  <input
                    id="preferred_ticketing_method_postal"
                    data-testid="trips-confirm-ticketing-method-postal"
                    className="companion-label"
                    type="radio"
                    value="postal"
                    onChange={e => updatePreferredTicketingMethod(e.target.value as 'qr' | 'postal')}
                    checked={store.reserveInfo.preferredTicketingMethod === 'postal'}
                  />
                  <span>チケット郵送（自宅・職場に届けてもらう）</span>
                </label>
              </div>
            </div>
          ))}

        {!isConfirmStep && store.reserveInfo.reservingTrip.isPackage && (
          <>
            <p style={styles.travelerPeopleNum}>
              ※パッケージ商品の新幹線チケットは日時の変更ができません。通常のチケットとは取扱いが異なりますのでご注意ください
            </p>
            <p>
              <br />
            </p>
          </>
        )}

        {!isConfirmStep && showQrGuidance && (
          <div style={styles.ticketingDeliveryBlock}>
            <div style={styles.fieldLabel} data-testid="trips-confirm-ticketing-method-address-label">
              切符送り先
            </div>
            <div style={styles.fieldValue} data-testid="trips-confirm-ticketing-method-qr-guidance">
              {isTabikobo(serviceId) ? (
                <>
                  【通常きっぷ】の新幹線チケットにつきましては、予約完了後、チャットにてQRコードを添付いたします。
                  <br />
                  添付したQRコードで駅の指定席券売機もしくは窓口にて発券をお願いいたします。
                  <br />
                  なお、発券方法等の詳細は、チャットにてご案内いたします。
                </>
              ) : (
                <>
                  新幹線チケットにつきましては、弊社で予約後、チャット画面にてQRコードを添付いたします。
                  <br />
                  添付したQRコードをもとに駅の指定席券売機で発券をお願いいたします。
                  <br />
                  なお、発券方法等の詳細は、チャットにてご案内いたします。
                </>
              )}
            </div>
          </div>
        )}

        {!isConfirmStep && showPostalTicketing && (
          <div style={styles.ticketingDeliveryBlock}>
            <div style={styles.fieldLabel} data-testid="trips-confirm-ticketing-method-address-label">
              切符送り先
            </div>
            <div>
              {store.reserveInfo.user.organization?.enabledCustomizeAddress && (
                <>
                  {!isConfirmStep && (
                    <label style={styles.addressTypeLabel}>
                      <input
                        id="shinkansen_text"
                        className="companion-label"
                        type="radio"
                        value="text"
                        onChange={e => {
                          store.reserveInfo.shinkansenAddressType = e.target.value as ShinkansenAddressType;
                        }}
                        checked={store.reserveInfo.shinkansenAddressType === 'text'}
                      />
                      <span>手動入力</span>
                    </label>
                  )}
                  {store.reserveInfo.shinkansenAddressType === 'text' && (
                    <div
                      style={styles.addressBlock}
                      data-testid="trips-confirm-ticketing-method-address-type-text"
                    >
                      {isConfirmStep ? (
                        <>
                          <div
                            style={styles.addressValue}
                            data-wovn-ignore
                            data-testid="trips-confirm-ticketing-method-postcode"
                          >
                            {store.reserveInfo.postcode}
                          </div>
                          <div
                            style={styles.addressValue}
                            data-wovn-ignore
                            data-testid="trips-confirm-ticketing-method-address"
                          >
                            {store.reserveInfo.shinkansenAddress}
                          </div>
                          <div
                            style={styles.addressValue}
                            data-wovn-ignore
                            data-testid="trips-confirm-ticketing-method-addressee"
                          >
                            {store.reserveInfo.shinkansenAddressee}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={styles.addressLabel}>郵便番号</div>
                          <Input
                            data-testid="trips-confirm-ticketing-method-postcode-input"
                            id="postcode"
                            type="text"
                            name="postcode"
                            fullWidth
                            value={store.reserveInfo.postcode}
                            onChange={e => {
                              store.reserveInfo.postcode = e.target.value;
                            }}
                          />
                          <div style={styles.addressLabel}>住所</div>
                          <Input
                            data-testid="trips-confirm-ticketing-method-address-input"
                            id="shinkansen_address"
                            type="text"
                            fullWidth
                            placeholder="チケットを送付するのに必要な情報まで記入ください（ビル名、部屋番号など）"
                            value={store.reserveInfo.shinkansenAddress}
                            onChange={e => {
                              store.reserveInfo.shinkansenAddress = e.target.value;
                            }}
                          />
                          <div style={styles.addressLabel}>宛名</div>
                          <Input
                            data-testid="trips-confirm-ticketing-method-addressee-input"
                            id="shinkansen_addressee"
                            type="text"
                            fullWidth
                            placeholder="個人宅への送付の場合は、送付先住所に加え、お受け取りの方のお名前をフルネームで記載をお願いします"
                            value={store.reserveInfo.shinkansenAddressee}
                            onChange={e => {
                              store.reserveInfo.shinkansenAddressee = e.target.value;
                            }}
                          />
                        </>
                      )}
                    </div>
                  )}
                </>
              )}

              {store.reserveInfo.departments && store.reserveInfo.departments.length > 0 && (
                <>
                  {!isConfirmStep && (
                    <label style={styles.addressTypeLabel}>
                      <input
                        id="shinkansen_select"
                        type="radio"
                        value="select"
                        onChange={e => {
                          store.reserveInfo.shinkansenAddressType = e.target.value as ShinkansenAddressType;
                        }}
                        checked={store.reserveInfo.shinkansenAddressType === 'select'}
                      />
                      <span>部署から選択</span>
                    </label>
                  )}
                  {store.reserveInfo.shinkansenAddressType === 'select' && (
                    <div
                      style={styles.addressBlock}
                      data-testid="trips-confirm-ticketing-method-address-type-select"
                    >
                      {isConfirmStep ? (
                        <>
                          <div
                            style={styles.addressValue}
                            data-wovn-ignore
                            data-testid="trips-confirm-ticketing-method-department-postcode"
                          >
                            {store.reserveInfo.organizationAddress &&
                              store.reserveInfo.organizationAddress.postalCode}
                          </div>
                          <div
                            style={styles.addressValue}
                            data-wovn-ignore
                            data-testid="trips-confirm-ticketing-method-department-address"
                          >
                            {store.reserveInfo.organizationAddress &&
                              store.reserveInfo.organizationAddress.location}
                          </div>
                          <div
                            style={styles.addressValue}
                            data-wovn-ignore
                            data-testid="trips-confirm-ticketing-method-department-addressee"
                          >
                            {store.reserveInfo.organizationAddress &&
                              store.reserveInfo.organizationAddress.addressee}
                          </div>
                        </>
                      ) : (
                        <SelectDepartments
                          departments={store.reserveInfo.departments}
                          onSelect={department => {
                            store.reserveInfo.organizationAddress = department.organizationAddress;
                          }}
                          disabledAddress
                          user={store.reserveInfo.user}
                          isRequired
                          workflowFormWidth={WORKFLOW_FORM_WIDTH}
                        />
                      )}
                    </div>
                  )}
                </>
              )}

              {store.organizationBases.length > 0 && (
                <>
                  {!isConfirmStep && (
                    <label style={styles.addressTypeLabel}>
                      <input
                        id="shinkansen_select_organization_base"
                        type="radio"
                        value="organization_base_select"
                        onChange={e => {
                          store.reserveInfo.shinkansenAddressType = e.target.value as ShinkansenAddressType;
                        }}
                        checked={store.reserveInfo.shinkansenAddressType === 'organization_base_select'}
                      />
                      <span>拠点から選択</span>
                    </label>
                  )}
                  {store.reserveInfo.shinkansenAddressType === 'organization_base_select' && (
                    <div
                      style={styles.addressBlock}
                      data-testid="trips-confirm-ticketing-method-address-type-organization-base-select"
                    >
                      {isConfirmStep ? (
                        <>
                          <div
                            style={styles.addressValue}
                            data-wovn-ignore
                            data-testid="trips-confirm-ticketing-method-organization-base-postcode"
                          >
                            {store.reserveInfo.organizationBase && store.reserveInfo.organizationBase.postalCode}
                          </div>
                          <div
                            style={styles.addressValue}
                            data-wovn-ignore
                            data-testid="trips-confirm-ticketing-method-organization-base-address"
                          >
                            {store.reserveInfo.organizationBase && store.reserveInfo.organizationBase.address}
                          </div>
                          <div
                            style={styles.addressValue}
                            data-wovn-ignore
                            data-testid="trips-confirm-ticketing-method-organization-base-addressee"
                          >
                            {store.reserveInfo.organizationBase && store.reserveInfo.organizationBase.addressee}
                          </div>
                        </>
                      ) : (
                        <SelectOrganizationBases
                          organizationBases={store.organizationBases}
                          onSelect={organizationBase => {
                            store.reserveInfo.organizationBase = organizationBase;
                          }}
                          isRequired
                        />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {store.reserveInfo.validationErrors.postcode && (
          <div style={styles.errorText}>{store.reserveInfo.validationErrors.postcode}</div>
        )}
        {store.reserveInfo.validationErrors.shinkansenAddress && (
          <div style={styles.errorText}>{store.reserveInfo.validationErrors.shinkansenAddress}</div>
        )}
      </div>
    </div>
  );
};

const getStyles = () => ({
  section: {
    margin: '0 0 24px',
    padding: 0
  },
  subtitle: {
    padding: 0,
    fontWeight: 'bold' as const,
    margin: '20px 0 10px',
    fontSize: 15
  },
  childContent: {
    padding: '0 20px',
    fontSize: 13
  },
  fieldBlock: {
    margin: 0
  },
  fieldLabel: {
    fontWeight: 400 as const,
    fontSize: 13,
    lineHeight: 1.4,
    marginBottom: 6
  },
  confirmFieldRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 24,
    margin: 0
  },
  confirmFieldLabel: {
    minWidth: 180,
    fontWeight: 400 as const,
    fontSize: 13,
    lineHeight: 1.4,
    marginBottom: 0
  },
  confirmFieldValue: {
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap' as const,
    fontSize: 13
  },
  fieldValue: {
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap' as const,
    fontSize: 13
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
    paddingLeft: 12
  },
  travelerType: {
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    lineHeight: 1.5,
    fontWeight: 600 as const
  },
  addressTypeLabel: {
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    lineHeight: 1.5,
    fontWeight: 600 as const,
    margin: '10px 0 5px'
  },
  travelerPeopleNum: {
    fontSize: 13
  },
  ticketingDeliveryBlock: {
    marginTop: 10
  },
  addressBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 6,
    flex: 1
  },
  addressLabel: {
    fontSize: 13,
    lineHeight: 1.5,
    marginTop: 2
  },
  addressValue: {
    fontSize: 13,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const
  },
  errorText: {
    marginTop: 8,
    fontSize: 13
  }
});

export default observer(TripsTicketingMethodSection);
